import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Route, Routes, useNavigate } from 'react-router-dom';
import axios from 'axios';
import './App.css';

interface Pensum {
  id: string;
  name?: string;
  imagePath?: string;
  description?: string;
  type?: string;
  beltColor: string;
  beltLevelNo: number;
}

interface GroupedData {
  [beltColor: string]: {
    [type: string]: Pensum[];
  };
}

function Pensum() {
  const [data, setData] = useState<Pensum[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate(); // Initialize useNavigate hook

  useEffect(() => {
    const fetchData = async () => {
      try {
        const username = process.env.REACT_APP_API_USERNAME;
        const password = process.env.REACT_APP_API_PASSWORD;
        const token = btoa(`${username}:${password}`); // Base64 encode username and password
        const response = await axios.get<Pensum[]>(`${process.env.REACT_APP_API_URL}/Pensum`,
          {
            headers: {
              Authorization: `Basic ${token}`,
            },
          }
        ); // Replace with your actual endpoint
        setData(response.data);
        setLoading(false);
      } catch (err) {
        setError('An error occurred while fetching data.');
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const groupByBeltAndType = (data: Pensum[]): GroupedData => {
    const grouped: GroupedData = data.reduce((acc: GroupedData, item) => {
      if (!acc[item.beltColor]) {
        acc[item.beltColor] = {};
      }
      const typeKey = item.type ?? 'No Type';
      if (!acc[item.beltColor][typeKey]) {
        acc[item.beltColor][typeKey] = [];
      }
      acc[item.beltColor][typeKey].push(item);
      return acc;
    }, {});

    // Reverse the type order within each beltColor
    Object.keys(grouped).forEach((beltColor) => {
      const reversedTypes = Object.entries(grouped[beltColor])
        .reverse()
        .reduce((acc, [type, items]) => {
          acc[type] = items;
          return acc;
        }, {} as { [type: string]: Pensum[] });
      grouped[beltColor] = reversedTypes;
    });

    return grouped;
  };

  const groupedData = data ? groupByBeltAndType(data) : null;

  return (
    <><div className="App">
      <header className="App-header">
        {loading && <p>Loading...</p>}
        {error && <p>{error}</p>}
        {groupedData && (
          <div className="content-container">
                    {/* Close button */}
        <button className="close-button" onClick={() => navigate('/')}>&times;</button>
            {data && groupedData &&
              Object.entries(groupedData)
                .sort(([aColor], [bColor]) => {
                  // Find a sample item for each beltColor to get beltLevelNo
                  const aSample = data.find(d => d.beltColor === aColor);
                  const bSample = data.find(d => d.beltColor === bColor);
                  return (aSample?.beltLevelNo ?? 0) - (bSample?.beltLevelNo ?? 0);
                })
                .map(([beltColor, types]) => (
                  <div key={beltColor} className="belt-group">
                    <h3>Bælte: {beltColor}</h3><hr />
                    {Object.entries(types).map(([type, items]) => (
                      <div key={type} className="type-group">
                        <h4>{type}</h4>
                        <ul className="data-list">
                          {items.map((item) => (
                            <li key={item.id} className="data-item">
                              <h5>{item.name ?? 'No Name'}</h5>
                              {item.imagePath && (
                                <img
                                  src={'img/' + item.imagePath}
                                  alt={item.name}
                                  className="item-image" />
                              )}
                              <p>{item.description ?? 'No Description'}</p>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                ))}
          </div>
        )}
      </header>
    </div>
    </>
  );
}

export default Pensum;
